import { Grid } from '@mui/material';
import { IPayrollShiftDifferentialRequests, gridTags, template } from 'app/models/Placement/PlacementMods';
import React, { useEffect, useState } from 'react';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { getPayrollShiftDiffGridColumns } from './helper';
import { usePromiseTracker } from 'react-promise-tracker';
import { XGrid } from '@AMIEWEB/Common/XGrid/XGrid';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrentModDetails,
  selectEditPayrollShiftDifferentialRows,
  selectExpandedModsDialog,
  selectIsCustomTemplateType,
  selectIsEditExistingMod,
  selectIsEditMod,
  selectIsUndoModal,
  selectOriginCustomTemplateChanged,
  selectShiftDifferentialAmountError,
  selectShiftDifferentialDescriptionTypeError,
  selectShiftDifferentialEndTimeError,
  selectShiftDifferentialStartTimeError,
  selectShiftDifferentialTypeError,
  selectShiftDifferentialDescriptionTypes,
  selectShiftDifferentialTemplateItems,
  selectShiftDifferentialTypes,
} from 'store/redux-store/placement-mods/selectors';
import { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';
import { placementModsAction } from 'store/redux-store/placement-mods/slice';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { generateRowId, getFormattedPayrollShiftDifferential } from '../../ModificationsUtils';
import { payrollShiftDiffModStyles } from './PayrollShiftDiffMod.styles';
import { useWatch } from 'react-hook-form';
import { isNullOrUndefined } from 'app/helpers/objectHelpers';
import { missingField } from 'app/constants';
import { TemplateTypes } from '../../CommonComponents/Utils';

export const PayrollShiftDiffGrid = ({
  payrollShiftDiffRequests,
  isTemplateChanged,
  setTemplateChanged,
}: {
  payrollShiftDiffRequests: IPayrollShiftDifferentialRequests[];
  isTemplateChanged: boolean;
  setTemplateChanged: any;
}) => {
  const { t } = useTranslation();

  const isExpanded = useSelector(selectExpandedModsDialog);
  const shiftDifferentialTypes = useSelector(selectShiftDifferentialTypes);
  const shiftDifferentialDescriptionTypes = useSelector(selectShiftDifferentialDescriptionTypes);
  const isModEdit = useSelector(selectIsEditMod);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const [errors, setErrors] = useState({});
  const selectedModsDetails = useSelector(selectCurrentModDetails);
  const [headerCount, setHeaderCount] = useState<number>(0);
  const { classes } = payrollShiftDiffModStyles({ isExpanded, isModEdit });
  const dispatch = useDispatch();
  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'payroll-shift-differential-api', delay: 0 });
  const [currentSortModel, setCurrentSortModel] = React.useState<any[]>([{ field: 'startDate', sort: 'desc' }]);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(true);
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const payrollShiftDifferentialEditRequests = useSelector(selectEditPayrollShiftDifferentialRows);
  const isUndoModal = useSelector(selectIsUndoModal);
  const isEditExistingMod = useSelector(selectIsEditExistingMod);
  const shiftDifferentialTemplateItems = useSelector(selectShiftDifferentialTemplateItems);
  const startTimeErrors = useSelector(selectShiftDifferentialStartTimeError);
  const endTimeErrors = useSelector(selectShiftDifferentialEndTimeError);
  const amountErrors = useSelector(selectShiftDifferentialAmountError);
  const typeErrors = useSelector(selectShiftDifferentialTypeError);
  const isCustomTemplateType = useSelector(selectIsCustomTemplateType);
  const originCustomTemplateChanged = useSelector(selectOriginCustomTemplateChanged);
  const descriptionTypeErrors = useSelector(selectShiftDifferentialDescriptionTypeError);
  const [columns, setColumns] = useState<any>(
    getPayrollShiftDiffGridColumns(t, isExpanded, isModEdit, classes, setErrors),
  );

  const templateValue: any = useWatch({ name: template });
  const rightActions = React.useMemo<IXGridBulkActionButton[]>(() => {
    return [
      {
        key: 'add-new-payroll-shift-differential',
        Icon: AddIcon,
        disabled: !isModEdit,
        onClick: e => {
          addNewRow(e);
        },
        tooltipProps: {
          tooltipContent: 'Add New Payroll Shift Differential',
        },
      },
      {
        key: 'delete',
        Icon: DeleteOutlinedIcon,
        disabled: !selectedIds || selectedIds?.length === 0,
        onClick: () => deleteHandler(),
        tooltipProps: {
          tooltipContent: 'Delete',
        },
      },
    ];
  }, [
    isExpanded,
    isModEdit,
    isDeleteDisabled,
    selectedIds,
    payrollShiftDifferentialEditRequests,
    startTimeErrors,
    endTimeErrors,
    amountErrors,
    typeErrors,
    descriptionTypeErrors,
  ]);

  const handleRefreshGrid = () => {
    resetData();
    dispatch(
      placementModsAction.getPayrollShiftDifferential({
        placementId: selectedModsDetails?.placementId,
        travelerOptionId: selectedModsDetails?.travelerOptionId,
        newMod: !isEditExistingMod,
      }),
    );
    selectedModsDetails.shiftDifferentialTemplateType === 'Standard'
      ? dispatch(placementModsAction.setCustomTemplateType(false))
      : dispatch(placementModsAction.setCustomTemplateType(true));
  };

  const getWidth = () => {
    if (isCallingAPI || !isExpanded) {
      return '930px';
    } else {
      return null;
    }
  };

  const getHeight = () => {
    if (!isCallingAPI || !isExpanded) {
      return '386px';
    } else {
      return null;
    }
  };

  const onRowsSelectionHandler = ids => {
    setSelectedIds(ids);
    const modData = isModEdit ? payrollShiftDifferentialEditRequests : payrollShiftDiffRequests;
    const selectedRowsData = ids.map(id => modData.find(row => row.id === id));
    selectedRowsData.length > 0 ? setIsDeleteDisabled(false) : setIsDeleteDisabled(true);
  };
  const deleteHandler = () => {
    const modData = isModEdit ? payrollShiftDifferentialEditRequests : payrollShiftDiffRequests;
    const updatedDataAfterDelete = modData.filter(item => !selectedIds.includes(item.id));
    const formattedData = getFormattedPayrollShiftDifferential(
      updatedDataAfterDelete,
      shiftDifferentialTypes,
      shiftDifferentialDescriptionTypes,
      isModEdit,
    );
    const updateAmount = amountErrors.filter(item => !selectedIds.includes(item.id));
    const updateStartTime = startTimeErrors.filter(item => !selectedIds.includes(item.id));
    const updateEndTime = endTimeErrors.filter(item => !selectedIds.includes(item.id));
    const updateType = typeErrors.filter(item => !selectedIds.includes(item.id));
    const updateDescriptionType = descriptionTypeErrors.filter(item => !selectedIds.includes(item.id));
    dispatch(placementModsAction.setGridModAltered(true));
    dispatch(placementModsAction.setCustomTemplateType(true));
    dispatch(placementModsAction.setOriginCustomTemplateChanged(true));
    dispatch(placementModsAction.setShiftDifferentialStartTimeError(updateStartTime));
    dispatch(placementModsAction.setShiftDifferentialEndTimeError(updateEndTime));
    dispatch(placementModsAction.setShiftDifferentialAmountError(updateAmount));
    dispatch(placementModsAction.setShiftDifferentialTypeError(updateType));
    dispatch(placementModsAction.setShiftDifferentialDescriptionTypeError(updateDescriptionType));

    dispatch(placementModsAction.setEditPayrollShiftDiffRows(formattedData));

    setSelectedIds([]);
  };
  const resetColumns = () => {
    setColumns(getPayrollShiftDiffGridColumns(t, isExpanded, isModEdit, classes, setErrors));
  };

  const addNewRow = e => {
    const existingRow = payrollShiftDifferentialEditRequests?.slice(0);
    const startTimeErrorsNew = startTimeErrors?.slice(0);
    const endTimeErrorsNew = endTimeErrors?.slice(0);
    const amountErrorsNew = amountErrors?.slice(0);
    const typeErrorsNew = typeErrors?.slice(0);
    const descriptionTypeErrorsNew = descriptionTypeErrors?.slice(0);
    const newRow = {
      id: `${generateRowId(selectedModsDetails.placementId)}`,
      placementId: selectedModsDetails?.placementId,
      travelerOptionId: selectedModsDetails?.travelerOptionId,
      shiftDifferentialId: null,
      shiftDifferentialDescriptionTypeId: '',
      shiftDifferentialTypeId: '',
      selectAll: 'No',
      startTime: '',
      endTime: '',
      amount: '',
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
      timestamp: '',
      placementTimestamp: '',
      approvalStatusId: '',
      voidStatus: '',
    };
    existingRow.unshift(newRow);
    const startTimeRow = {
      startTime: true,
      id: newRow?.id,
    };
    const endTimeRow = {
      endTime: true,
      id: newRow?.id,
    };
    const amountRow = {
      amount: true,
      id: newRow?.id,
    };
    const typeRow = {
      type: true,
      id: newRow?.id,
    };
    const descriptionTypeRow = {
      descriptionType: true,
      id: newRow?.id,
    };
    startTimeErrorsNew.unshift(startTimeRow);
    endTimeErrorsNew.unshift(endTimeRow);
    amountErrorsNew.unshift(amountRow);
    typeErrorsNew.unshift(typeRow);
    descriptionTypeErrorsNew.unshift(descriptionTypeRow);
    const formattedData = getFormattedPayrollShiftDifferential(
      existingRow,
      shiftDifferentialTypes,
      shiftDifferentialDescriptionTypes,
      isModEdit,
    );
    dispatch(placementModsAction.setGridModAltered(true));
    dispatch(placementModsAction.setCustomTemplateType(true));
    dispatch(placementModsAction.setOriginCustomTemplateChanged(true));
    dispatch(placementModsAction.setShiftDifferentialStartTimeError(startTimeErrorsNew));
    dispatch(placementModsAction.setShiftDifferentialEndTimeError(endTimeErrorsNew));
    dispatch(placementModsAction.setShiftDifferentialAmountError(amountErrorsNew));
    dispatch(placementModsAction.setShiftDifferentialTypeError(typeErrorsNew));
    dispatch(placementModsAction.setShiftDifferentialDescriptionTypeError(descriptionTypeErrorsNew));
    dispatch(placementModsAction.setEditPayrollShiftDiffRows(formattedData));
  };

  const resetData = () => {
    setTemplateChanged(false);
    dispatch(placementModsAction.setShiftDifferentialStartTimeError([]));
    dispatch(placementModsAction.setShiftDifferentialEndTimeError([]));
    dispatch(placementModsAction.setShiftDifferentialAmountError([]));
    dispatch(placementModsAction.setShiftDifferentialTypeError([]));
    dispatch(placementModsAction.setShiftDifferentialDescriptionTypeError([]));
    dispatch(placementModsAction.setOriginCustomTemplateChanged(false));
    dispatch(placementModsAction.setTemplateChangedInShift(false));
    dispatch(placementModsAction.setGridModError(false));
    dispatch(placementModsAction.setIsUndoModal(false));
  };
  const getHeaderCount = payrollShiftDifferentialEditRequests => {
    const totalRowCount = payrollShiftDifferentialEditRequests?.length;
    const nullRowCount = payrollShiftDifferentialEditRequests?.filter(
      each =>
        each?.amount === '' ||
        each?.amount === null ||
        isNullOrUndefined(each?.startTime) ||
        each?.starTime === missingField ||
        isNullOrUndefined(each?.endTime) ||
        each?.endTime === missingField ||
        each?.shiftDifferentialTypeId === '' ||
        each?.shiftDifferentialTypeId === null ||
        each?.shiftDifferentialDescriptionTypeId === '' ||
        each?.shiftDifferentialDescriptionTypeId === null,
    )?.length;
    setHeaderCount(totalRowCount - nullRowCount);
  };

  const checkAmountError = () => {
    let arr = [];
    for (let value of payrollShiftDifferentialEditRequests) {
      if (!value?.amount && value.amount !== 0) {
        arr.push({ amount: true, id: value.id });
      }
    }
    dispatch(placementModsAction.setShiftDifferentialAmountError(arr));
    dispatch(placementModsAction.setTemplateChangedInShift(false));
  };
  useEffect(() => {
    checkAmountError();
  }, []);
  useEffect(() => {
    if (!isCustomTemplateType) {
      checkAmountError();
    }
  }, [isTemplateChanged, payrollShiftDifferentialEditRequests, isUndoModal]);
  useEffect(() => {
    const initialColumns = getPayrollShiftDiffGridColumns(t, isExpanded, isModEdit, classes, setErrors);
    setColumns(initialColumns);
  }, [t, isExpanded, isModEdit]);

  useEffect(() => {
    if (payrollShiftDifferentialEditRequests?.length === 0) {
      dispatch(placementModsAction.setGridModError(true));
    }
    if (isModEdit) {
      setCurrentSortModel([]);
    }
    if (isTemplateChanged) dispatch(placementModsAction.setTemplateChangedInShift(true));
    getHeaderCount(isModEdit ? payrollShiftDifferentialEditRequests : payrollShiftDiffRequests);
  }, [payrollShiftDifferentialEditRequests]);

  useEffect(() => {
    if (isModEdit && isTemplateChanged) {
      if (isCustomTemplateType && !originCustomTemplateChanged) {
        dispatch(placementModsAction.setEditPayrollShiftDiffRows([]));
        setTemplateChanged(false);
        dispatch(placementModsAction.setTemplateChangedInShift(false));
      } else dispatch(placementModsAction.setOriginCustomTemplateChanged(false));
    }
  }, [isCustomTemplateType]);

  useEffect(() => {
    if (isUndoModal) {
      resetData();
    }
  }, [isUndoModal]);

  useEffect(() => {
    if (
      isModEdit &&
      templateValue !== null &&
      parseInt(templateValue.toString()) == TemplateTypes.Standard &&
      isTemplateChanged &&
      !isUndoModal
    ) {
      const formattedData = getFormattedPayrollShiftDifferential(
        shiftDifferentialTemplateItems,
        shiftDifferentialTypes,
        shiftDifferentialDescriptionTypes,
        isModEdit,
      );
      dispatch(placementModsAction.setEditPayrollShiftDiffRows(formattedData));
      setSelectedIds([]);
    }
  }, [templateValue, isTemplateChanged]);

  useEffect(() => {
    if (payrollShiftDifferentialEditRequests?.length === 0) {
      dispatch(placementModsAction.setGridModError(true));
    } else if (
      amountErrors?.some(val => val.amount) ||
      startTimeErrors.some(val => val.startTime) ||
      endTimeErrors.some(val => val.endTime) ||
      typeErrors.some(val => val.type) ||
      descriptionTypeErrors.some(val => val.descriptionType)
    ) {
      dispatch(placementModsAction.setGridModError(true));
    } else {
      dispatch(placementModsAction.setGridModError(false));
    }
  }, [amountErrors, startTimeErrors, endTimeErrors, descriptionTypeErrors, payrollShiftDifferentialEditRequests]);

  const PayrollShiftDiffGrid = React.useMemo(() => {
    return (
      <Grid style={{ paddingBottom: '24px' }}>
        <XGrid
          gridTag={gridTags.PAYROLLSHIFTDIFFERENTIALGRIDTAG}
          rows={
            shiftDifferentialTypes &&
            shiftDifferentialDescriptionTypes &&
            getFormattedPayrollShiftDifferential(
              isModEdit ? payrollShiftDifferentialEditRequests : payrollShiftDiffRequests,
              shiftDifferentialTypes,
              shiftDifferentialDescriptionTypes,
              isModEdit,
            )
          }
          columns={columns}
          disableSelectionOnClick
          pagination={false}
          autoHeight={true}
          hideFooter
          editMode="row"
          inDialog
          title={t('placement.placementMods.payrollShiftDifferential')}
          sortModel={currentSortModel}
          onSortModelChange={model => {
            setCurrentSortModel(model);
          }}
          displayCustomCount={selectedIds?.length > 0 ? selectedIds?.length : headerCount}
          loading={isCallingAPI}
          className={classes.expandedGrid}
          onSelectionModelChange={ids => onRowsSelectionHandler(ids)}
          checkboxSelection={isModEdit}
          selectionModel={selectedIds}
          toolbarProps={{
            rightActions: rightActions,
            columnSelector: isModEdit ? false : true,
            refreshButton: true,
            resetColumns: () => {
              resetColumns();
              dispatch(
                globalActions.setSnackBar({
                  message: 'Columns have been reset.',
                  severity: 'success',
                }),
              );
            },
            refreshGrid: () => {
              handleRefreshGrid();
            },
          }}
        />
      </Grid>
    );
  }, [
    payrollShiftDiffRequests,
    payrollShiftDifferentialEditRequests,
    isExpanded,
    isCallingAPI,
    resetColumns,
    columns,
    currentSortModel,
  ]);

  return (
    <Grid item xs={12} sx={{ width: getWidth(), height: getHeight() }}>
      {PayrollShiftDiffGrid}
    </Grid>
  );
};
